<script setup lang="ts">
import { ref, watch } from "vue";
import { Facade } from "@/drawing";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/** Cote sélectionnée : affiche sa valeur et permet de la forcer. */
const props = defineProps<{
  target: string;
}>();

const measured = ref("");
const forced = ref("");
const input = ref("");

// Le Facade n'est pas réactif : relu à chaque nouvelle cible et après chaque
// changement fait ici.
function refresh() {
  const text = Facade.isInitialized ? Facade.dimensions.getSelectedText() : null;
  measured.value = text?.measured ?? "-";
  forced.value = text?.forced ?? "";
  input.value = forced.value;
}

watch(() => props.target, refresh, { immediate: true });

function setText(text: string) {
  if (!Facade.isInitialized) return;
  Facade.dimensions.setSelectedText(text);
  refresh();
}
</script>

<template>
  <button
    type="button"
    class="canvas-btn canvas-btn-nosize dropdown-toggle value-btn"
    :class="{ forced: !!forced }"
    data-bs-toggle="dropdown"
    aria-expanded="false"
    @click="refresh()"
  >
    <PhosphorIcon v-if="forced" name="push-pin" :size="14" />
    {{ forced || measured }}
  </button>
  <ul class="dropdown-menu canvas-dropdown">
    <li>
      <h6 class="dropdown-header">
        {{ $t("canvas:floating.dimension.header") }}
      </h6>
    </li>
    <li>
      <div class="input-group p-1">
        <input
          type="text"
          class="form-control"
          v-model="input"
          :placeholder="measured"
          style="width: 90px"
          @keydown.enter="setText(input.trim())"
        />
        <span
          type="button"
          class="input-group-text"
          @click="setText(input.trim())"
        >
          <PhosphorIcon name="check" :size="14" />
        </span>
      </div>
    </li>
    <li v-if="forced">
      <button type="button" class="dropdown-item" @click="setText('')">
        {{ $t("canvas:floating.dimension.reset", { value: measured }) }}
      </button>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
// Toute la hauteur de la barre, comme les autres boutons.
.value-btn {
  height: 36px;
  padding: 0 10px;
  gap: 4px;

  // Valeur forcée : repérable d'un coup d'oeil.
  &.forced {
    color: var(--bs-warning);
  }
}

.canvas-dropdown {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  color: white;

  .dropdown-header {
    color: rgba(255, 255, 255, 0.4);
  }

  .dropdown-item {
    color: white;
    font-size: 13px;
    // Survol à la souris seulement (au doigt, :hover reste collé après l'appui).
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background-color: var(--canvas-bg-hover);
      }
    }
  }

  .input-group-text {
    background-color: var(--canvas-bg-hover);
    border-color: var(--canvas-border);
    color: white;
    cursor: pointer;
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background-color: var(--canvas-btn-active-bg);
      }
    }
  }

  .form-control {
    background-color: var(--canvas-bg-hover);
    border-color: var(--canvas-border);
    color: white;
    &:focus {
      background-color: var(--canvas-bg-hover) !important;
      color: white !important;
      box-shadow: none;
      border-color: var(--canvas-border);
    }
  }
}
</style>
